import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminBusinessUpdate,
  AdminBusinessUpdateListItem,
  AdminPaginatedEnvelope,
  BusinessUpdateListQuery,
  CreateAdminBusinessUpdateInput,
  MediaPresignResponse,
  UpdateAdminBusinessUpdateInput,
} from '@/types';

const commonEndpoint = '/v2/admin/updates';

export type BusinessUpdateListResponse = AdminPaginatedEnvelope<
  AdminBusinessUpdateListItem[]
>;

export const businessUpdateService = {
  getList: async (
    filters: BusinessUpdateListQuery,
  ): Promise<BusinessUpdateListResponse> => {
    const {status, ...rest} = filters;
    // Omit status entirely when unset: toQueryString serializes undefined as "undefined", which 400s the backend's Joi schema.
    const params = {
      page: 0,
      limit: 10,
      ...rest,
      ...(status ? {status} : {}),
    };
    const url = `${commonEndpoint}?${toQueryString(params)}`;
    const response = await axios.get<BusinessUpdateListResponse>(url);
    return response.data;
  },

  getItem: async (updateId: string): Promise<AdminBusinessUpdate> => {
    const response = await axios.get<AdminBusinessUpdate>(
      `${commonEndpoint}/${updateId}`,
    );
    return response.data;
  },

  createItem: async (
    body: CreateAdminBusinessUpdateInput,
  ): Promise<AdminBusinessUpdate> => {
    const response = await axios.post<AdminBusinessUpdate>(
      commonEndpoint,
      body,
    );
    return response.data;
  },

  updateItem: async (
    updateId: string,
    body: UpdateAdminBusinessUpdateInput,
  ): Promise<AdminBusinessUpdate> => {
    const response = await axios.patch<AdminBusinessUpdate>(
      `${commonEndpoint}/${updateId}`,
      body,
    );
    return response.data;
  },

  publishNow: async (updateId: string): Promise<AdminBusinessUpdate> => {
    const response = await axios.post<AdminBusinessUpdate>(
      `${commonEndpoint}/${updateId}/publish`,
    );
    return response.data;
  },

  unpublish: async (updateId: string): Promise<AdminBusinessUpdate> => {
    const response = await axios.post<AdminBusinessUpdate>(
      `${commonEndpoint}/${updateId}/unpublish`,
    );
    return response.data;
  },

  deleteItem: async (updateId: string): Promise<void> => {
    await axios.delete(`${commonEndpoint}/${updateId}`);
  },

  // `name` MUST start with `business-updates/` or the backend 400s.
  presignMedia: async (name: string): Promise<MediaPresignResponse> => {
    const response = await axios.post<MediaPresignResponse>(
      `${commonEndpoint}/media/presign`,
      {
        name,
        mode: 'putObject',
      },
    );
    return response.data;
  },
};
